import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IComplaint } from "../../exports/interfaces";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import Select from "react-select";
import { Upload } from "lucide-react";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useLoader } from "../../context/LoaderContext";
import { fetchComplaints } from "../../api/ComplaintApi";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { useOption } from "../../context/OptionContext";
import { renderExistingFilePreview, renderFilePreview } from "../../exports/functions";

const ComplaintsDetails = ({
  complaint,
  urgentCount,
  setUrgentCount,
  notUrgentCount,
  setNotUrgentCount,
  regularCount,
  setRegularCount,
}: {
  complaint: IComplaint | null;
  urgentCount: number;
  setUrgentCount: Dispatch<SetStateAction<number>>;
  notUrgentCount: number;
  setNotUrgentCount: Dispatch<SetStateAction<number>>;
  regularCount: number;
  setRegularCount: Dispatch<SetStateAction<number>>;
}) => {
  const { setLoading } = useLoader();
  const { categories } = useOption();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<IComplaint | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            fileName: file.name,
            file,
          }
        : prev,
    );
  };

  useEffect(() => {
    if (complaint) {
      setFormData(complaint);
    }
  }, [complaint]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            [name]: value,
          }
        : prev,
    );
  };

  const complaintTypeOptions =
    categories
      ?.find((item) => item.name === "Complaint Type")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const priorityOptions =
    categories
      ?.find((item) => item.name === "Complaint Priority")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const cooperativeTypeOptions = [
    { value: "All Cooperative", label: "All Cooperative" },
    { value: "Agricultural Cooperative", label: "Agricultural Cooperative" },
    {
      value: "Saving and Credit Cooperative",
      label: "Saving and Credit Cooperative",
    },
    { value: "Multipurpose Cooperative", label: "Multipurpose Cooperative" },
    { value: "Other Cooperative", label: "Other Cooperative" },
  ];

  const handleSelectChange = (
    name: keyof IComplaint,
    selected: SelectOption | null,
  ) => {
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            [name]: selected?.value,
          }
        : prev,
    );
  };

  const handleUpdate = async () => {
    if (!formData) return;

    setLoading(true);

    try {
      const payload = new FormData();

      payload.append("complainantName", formData.complainantName || "");
      payload.append("complainantContact", formData.complainantContact || "");
      payload.append("email", formData.email || "");

      payload.append("complaintType", formData.complaintType || "");
      payload.append("complaintPriority", formData.complaintPriority || "");
      payload.append("complaintArea", formData.complaintArea || "");

      payload.append("cooperativeName", formData.cooperativeName || "");
      payload.append(
        "cooperativeRegistrationNumber",
        formData.cooperativeRegistrationNumber || "",
      );

      payload.append("complainDetails", formData.complainDetails || "");
      payload.append("filedOn", formData.filedOn || "");

      if (formData.file) {
        payload.append("documentUrl", formData.file);
      }

      const response = await axiosClient.put(
        `/complaints/${formData.id}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 200 || response.status === 201) {
        fetchComplaints()
          .then((complaintData) => {
            const urgent = complaintData.filter(
              (n) => n.status === "Urgent",
            ).length;

            const notUrgent = complaintData.filter(
              (n) => n.status === "Not Urgent",
            ).length;

            const regular = complaintData.filter(
              (n) => n.status === "Regular",
            ).length;

            setUrgentCount(urgent);
            setNotUrgentCount(notUrgent);
            setRegularCount(regular);

            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Complaint updated successfully!");
            }, 1000);
          })
          .catch((error) => {
            setTimeout(() => {
              setLoading(false);
              dynamicFailed(`${error.message}`);
            }, 1000);
          });
      }
    } catch (error: any) {
      setTimeout(() => {
        setLoading(false);
        dynamicFailed(`${error.message}`);
      }, 1000);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Complain Details</h3>
          <small>
            {urgentCount} URGENT, {regularCount} REGULAR, {notUrgentCount} NOT
            URGENT
          </small>
        </div>
      </div>

      <div className="dashboard-card p-3 mt-3">
        <h5>Complain Handling</h5>
        <div className="row g-3">
          <Input
            type="text"
            label="Complainant Name"
            name="complainantName"
            value={formData?.complainantName}
            placeholder="Enter Complaints here"
            col={6}
            onChange={handleChange}
          />

          <Input
            type="text"
            label="Mobile Number"
            name="complainantContact"
            value={formData?.complainantContact}
            placeholder="Enter mobile number here"
            col={6}
            onChange={handleChange}
          />

          <Input
            type="email"
            label="Email Address"
            name="email"
            value={formData?.email}
            placeholder="Enter Email Address here"
            col={6}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="dashboard-card p-3 mt-3">
        <h5>Complaint Details</h5>

        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">
              Complaint Type <span className="primary-color">*</span>
            </label>
            <Select
              styles={customSelectStyles}
              options={complaintTypeOptions}
              placeholder="Select Complaint Type"
              value={
                complaintTypeOptions.find(
                  (option) => option.value === formData?.complaintType,
                ) || null
              }
              onChange={(selected) =>
                handleSelectChange("complaintType", selected)
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">
              Complaint Priority <span className="primary-color">*</span>
            </label>
            <Select
              styles={customSelectStyles}
              options={priorityOptions}
              placeholder="Select Complaint Priority"
              value={
                priorityOptions.find(
                  (option) => option.value === formData?.complaintPriority,
                ) || null
              }
              onChange={(selected) =>
                handleSelectChange("complaintPriority", selected)
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">
              Complaint Area <span className="primary-color">*</span>
            </label>
            <Select
              styles={customSelectStyles}
              options={cooperativeTypeOptions}
              placeholder="Select Complaint Area"
              value={
                cooperativeTypeOptions.find(
                  (option) => option.value === formData?.complaintArea,
                ) || null
              }
              onChange={(selected) =>
                handleSelectChange("complaintArea", selected)
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <Input
            type="text"
            label="Cooperative Name"
            name="cooperativeName"
            value={formData?.cooperativeName}
            placeholder="Enter Street\tole. here"
            col={4}
            onChange={handleChange}
          />

          <Input
            type="text"
            label="Cooperative Registration Number"
            name="cooperativeRegistrationNumber"
            value={formData?.cooperativeRegistrationNumber}
            placeholder="Enter Administrator email here"
            col={4}
            onChange={handleChange}
          />

          <div className="col-md-4">
            <label className="form-label">Received Date</label>
            <input
              className="form-control"
              type="date"
              value={formData?.filedOn || ""}
              onChange={(e) =>
                setFormData((prev) =>
                  prev ? { ...prev, filedOn: e.target.value } : prev,
                )
              }
            />
          </div>
          <div className="col-12">
            <label className="form-label">Complain Details</label>
            <textarea
              className="form-control"
              rows={4}
              value={formData?.complainDetails}
              name="complainDetails"
              placeholder="Enter Notice Details here"
              onChange={(e) =>
                setFormData((prev) =>
                  prev ? { ...prev, complainDetails: e.target.value } : prev,
                )
              }
            />
          </div>
          <div className="col-12">
            <label className="form-label">Complain (PDF, JPEG, PNG)</label>

            <div
              className={`upload-box p-4 ${!formData?.file && !formData?.documentUrl && "text-center"}`}
            >
              {formData?.file ? (
                renderFilePreview(formData.file, formData.fileName, (e: any) =>
                  handleFileChange(e),
                )
              ) : formData?.documentUrl ? (
                renderExistingFilePreview(
                  formData.documentUrl,
                  handleFileChange,
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 100 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse file
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e);
                      }}
                    />
                  </label>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="text-end mt-3">
          <button
            className="btn btn-badge-border px-3 me-2"
            onClick={() => navigate("/complaints-list")}
          >
            Cancel
          </button>
          <button className="btn btn-badge-primary px-3" onClick={handleUpdate}>
            Update
          </button>
        </div>
      </div>

      {/* <div className="text-end mt-3">
        <button
          className="btn btn-badge-primary px-3"
          onClick={() => navigate("/complaints-list")}
        >
          Back to list
        </button>
      </div> */}
    </>
  );
};

const Input = ({
  type,
  label,
  name,
  value,
  placeholder,
  col,
  onChange,
  disabled,
}: {
  type: string;
  label: React.ReactNode;
  name: string;
  value?: string | number;
  placeholder?: string;
  col: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}) => (
  <div className={`col-md-${col}`}>
    <label className="form-label">
      {label} <span className="primary-color">*</span>
    </label>
    <input
      type={type}
      name={name}
      className="form-control"
      value={value || ""}
      placeholder={placeholder}
      onChange={onChange}
      disabled={disabled}
    />
  </div>
);

export default ComplaintsDetails;
